import {
    AppCard,
    Button,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
} from "@jhool-io/fe-components";
import { useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useIsMutating } from "@tanstack/react-query";
import FilterButton from "../../../../../shared-ui/Buttons/FilterButton/FilterButton";
import { useAppSelector } from "../../../../../hooks/useRedux";
import ListState from "../../../../../components/ListState/ListState";
import PlusIcon from "../../../../../components/Icons/Plus";
import UpcomingAppointment from "./UpcomingAppointment/UpcomingAppointment";
import { makeStringFirstLetterCapital } from "../../../../../utils/helpers";
import ConfirmScheduleForm from "../../ConfirmScheduleForm/ConfirmScheduleForm";
import CalendarDays from "../../../../../components/Icons/SideNav/CalendarDays";
import { GetStartAndEndOfCurrentWeek } from "../../../../../utils/moment";
import useNewBackground from "../../../../../hooks/useNewBackground";
import { useFetchProviderUpcomingSessions } from "../../../../providers/hooks/providers.queries";

export default function UpcomingAppointments() {
    const [showDialog, setShowDialog] = useState(false);

    const [searchParams, setSearchParams] = useSearchParams();

    const { startOfWeek, endOfWeek } = GetStartAndEndOfCurrentWeek();

    const { practice } = useAppSelector((state) => state.userPractice);

    const useNewBg = useNewBackground();

    const paymentFilter = searchParams.get("payment") || "";

    const confirmScheduleIsMutating = useIsMutating(["confirm-schedule"]);

    const { data, isLoading, error, isSuccess } =
        useFetchProviderUpcomingSessions(
            practice?.provider_id || "",
            {
                from_date: startOfWeek,
                to_date: endOfWeek,
            },
            Boolean(practice?.provider_id)
        );

    const handleSetPaymentFilter = (payment: string) => {
        if (payment === "") searchParams.delete("payment");
        else searchParams.set("payment", payment);

        setSearchParams(searchParams);
    };

    const filteredSessions =
        data?.data.filter((session) => {
            if (paymentFilter === "paid") return session.is_paid;
            if (paymentFilter === "unpaid") return !session.is_paid;
            return true;
        }) || [];

    return (
        <>
            <Dialog open={showDialog}>
                <DialogContent
                    title="Create this week’s schedule"
                    variant="center"
                    handleCloseDialog={() => setShowDialog(false)}
                    wrapperClass="w-[800px]"
                    saveText="Confirm schedule"
                    submitBtnFormValue="confirm-schedule"
                    isSubmitBtnDisabled={confirmScheduleIsMutating > 0}
                    isCancelBtnDisabled={confirmScheduleIsMutating > 0}
                >
                    <ConfirmScheduleForm
                        onFormSubmit={() => setShowDialog(false)}
                    />
                </DialogContent>
            </Dialog>
            <AppCard
                mode={useNewBg ? "new" : "old"}
                oldCardClass="border border-strokedark bg-white"
                className="rounded-r12 flex flex-col relative overflow-y-auto h-[400px] lg:h-[430px]"
            >
                <div className="px-16 sticky top-0 left-0 z-[5]">
                    <div className="flex justify-between items-center flex-wrap gap-16 py-20 border-b border-b-strokedark">
                        <h3 className="text-base font-medium tracking-[-0.176px] leading-[24px]">
                            Upcoming Appointment
                        </h3>
                        <div className="flex gap-x-8 items-center flex-wrap gap-y-16">
                            <Dropdown>
                                <DropdownTrigger asChild disabled={isLoading}>
                                    <FilterButton
                                        text={
                                            paymentFilter
                                                ? makeStringFirstLetterCapital(
                                                      paymentFilter
                                                  )
                                                : "Payment"
                                        }
                                        mode={useNewBg ? "new" : "old"}
                                    />
                                </DropdownTrigger>
                                <DropdownContent
                                    mode={useNewBg ? "new" : "old"}
                                >
                                    <DropdownItem
                                        onClick={() =>
                                            handleSetPaymentFilter("")
                                        }
                                        isSelected={!paymentFilter}
                                    >
                                        All
                                    </DropdownItem>
                                    <DropdownItem
                                        onClick={() =>
                                            handleSetPaymentFilter("paid")
                                        }
                                        isSelected={paymentFilter === "paid"}
                                    >
                                        Paid
                                    </DropdownItem>
                                    <DropdownItem
                                        onClick={() =>
                                            handleSetPaymentFilter("unpaid")
                                        }
                                        isSelected={paymentFilter === "unpaid"}
                                    >
                                        Unpaid
                                    </DropdownItem>
                                </DropdownContent>
                            </Dropdown>
                            <Button
                                className="gap-x-8"
                                onClick={() => setShowDialog(true)}
                            >
                                <CalendarDays
                                    className="size-18"
                                    stroke="#ffffff"
                                    fill="#ffffff"
                                />
                                Create Schedule
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="flex-1 p-16 overflow-y-auto">
                    {isLoading ? (
                        <div
                            className="flex items-center justify-center h-full"
                            data-testid="loader"
                        >
                            <span className="mid-loader" />
                        </div>
                    ) : null}
                    {error ? (
                        <div className="h-full text-sm font-medium flex items-center justify-center">
                            <ListState
                                isError
                                errorMsg={
                                    error?.response?.data?.message ||
                                    "Could not load schedule at this time"
                                }
                                stateHelperText="Try reloading this page to solve this issue"
                                context="general"
                                mode={useNewBg ? "new" : "old"}
                            />
                        </div>
                    ) : null}
                    {isSuccess && filteredSessions.length === 0 ? (
                        <ListState
                            stateHelperText={`Click the "+Create schedule" button to create and confirm your schedule for the week.`}
                            isError={false}
                            emptyMessage="No schedule found"
                            context="general"
                            mode={useNewBg ? "new" : "old"}
                            emptyBtnProps={{
                                showButton: true,
                                buttonText: (
                                    <>
                                        <PlusIcon stroke="#5A5F6A" />
                                        Create schedule
                                    </>
                                ),
                                buttonClass:
                                    "gap-x-8 bg-white border border-strokedark text-text-sub hover:bg-secondary-light",
                                onButtonClick: () => setShowDialog(true),
                            }}
                        />
                    ) : null}
                    {isSuccess && filteredSessions.length > 0 ? (
                        <div className="flex flex-col gap-y-12">
                            {filteredSessions.map((appointment, index) => (
                                <UpcomingAppointment
                                    key={`${appointment.session_id} ${
                                        index + 1
                                    }`}
                                    appointment={appointment}
                                />
                            ))}
                        </div>
                    ) : null}
                </div>
            </AppCard>
        </>
    );
}
